import { setCurrentUser } from './auth'

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

/**
 * Wrapper único para llamadas a la API (Sprint 16: cookie httpOnly).
 *
 * - credentials:'include': el navegador adjunta la cookie de sesión httpOnly.
 *   Ya no existe token en JS — nada que inyectar en headers.
 * - Ante 401 (sesión caducada/revocada): limpia el cache de usuario en
 *   memoria y redirige a /login con `?expired=1` (LoginPage muestra el aviso
 *   de sesión caducada) y `?next=<ruta+search>` para volver al deep-link tras
 *   re-autenticar. `next` solo lleva rutas internas (empiezan por '/' y no
 *   por '//'). No tira excepción para evitar que la página parpadee con un
 *   error técnico antes de la redirección (promesa nunca-resuelta,
 *   intencional).
 * - 403 forbidden_origin (CSRF) NO redirige: es un error de configuración
 *   (WEB_ORIGINS/CORS_ORIGIN) que debe verse, no esconderse tras un login.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    // Content-Type SOLO si hay body: con el header presente y body vacío
    // (POST/DELETE sin payload) Fastify ejecuta el parser JSON y responde
    // 400 FST_ERR_CTP_EMPTY_JSON_BODY antes de llegar a la ruta.
    ...(init?.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...((init?.headers as Record<string, string>) ?? {}),
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (res.status === 401) {
    setCurrentUser(null)
    // No redirigir si ya estamos en /login para no entrar en loop.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      // Deep-link: preservamos ruta+search para que LoginPage vuelva ahí tras
      // re-autenticar. Whitelist anti open-redirect: solo rutas internas
      // ('/'-relativas, nunca '//host' ni esquemas). LoginPage re-valida.
      const next = window.location.pathname + window.location.search
      const nextParam =
        next.startsWith('/') && !next.startsWith('//')
          ? `&next=${encodeURIComponent(next)}`
          : ''
      window.location.href = `/login?expired=1${nextParam}`
    }
    // Devolvemos una promesa nunca-resolved para que el caller no procese.
    // Lanzar excepción aquí provocaría un flash de error en la UI antes de
    // que el browser haga el redirect.
    return new Promise<T>(() => {})
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`API ${res.status} ${res.statusText}: ${text}`)
  }
  return res.json() as Promise<T>
}

/**
 * Wrapper para páginas PÚBLICAS (DSR form, sub-procesadores, formularios
 * públicos legales). credentials:'omit': un admin probando la página pública
 * NO debe enviar su cookie de sesión al endpoint público (mismo espíritu que
 * cuando no se inyectaba el Bearer). Tampoco redirige a /login en 401 — la
 * página pública es stateless.
 */
export async function publicApi<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    // Mismo criterio que api(): Content-Type solo con body real.
    ...(init?.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...((init?.headers as Record<string, string>) ?? {}),
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'omit' })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`API ${res.status} ${res.statusText}: ${text}`)
  }
  return res.json() as Promise<T>
}
